import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Star, Loader2, Eye, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { MillieAvatar } from "./MascotBubble";

export function useRecap(activeId) {
  const [recap, setRecap] = useState(null);
  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/recap/${activeId}`); setRecap(data); } catch { /* optional */ }
  }, [activeId]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    const { data } = await api.get(`/recap/${activeId}`, { params: { generate: true } });
    setRecap(data);
    return data;
  };
  const markRead = async () => {
    if (!recap || recap.read) return;
    setRecap({ ...recap, read: true });
    try { await api.post(`/recap/${activeId}/read`); } catch { /* ignore */ }
  };
  return { recap, generate, markRead, reload: load };
}

export default function RecapSheet({ open, onClose, recap, generate, markRead, notifications = [], onReadNotifications }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [noticed, setNoticed] = useState([]);
  useEffect(() => { setNoticed(recap?.noticed || []); }, [recap]);
  const keep = async (m) => {
    try { await api.put(`/memories/${m.id}`, { text: m.text }); setNoticed((p) => p.filter((x) => x.id !== m.id)); toast.success("Kept — Millie will plan around it"); }
    catch { toast.error("Couldn't save that"); }
  };
  const forget = async (m) => {
    try { await api.delete(`/memories/${m.id}`); setNoticed((p) => p.filter((x) => x.id !== m.id)); toast("Forgotten"); }
    catch { toast.error("Couldn't forget that"); }
  };

  useEffect(() => { if (open && recap) markRead(); if (open && notifications.some((n) => !n.read)) onReadNotifications?.(); }, [open, recap, markRead, notifications, onReadNotifications]);

  const make = async () => { setLoading(true); try { await generate(); } finally { setLoading(false); } };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          className="fixed inset-0 z-50 bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" data-testid="recap-sheet">
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <MillieAvatar size={44} float={false} mood="love" />
              <div className="flex-1">
                <p className="font-hand text-2xl text-sorted-ink leading-none">Millie's notes</p>
                <p className="text-xs text-sorted-slate font-semibold">{recap ? recap.week : "Your week in little wins"}</p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="recap-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
            </div>

            {notifications.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="notification-list">
                {notifications.slice(0, 6).map((n) => (
                  <button key={n.id} onClick={() => { onClose(); navigate(n.url || "/home"); }} data-testid={`notification-${n.id}`}
                    className={`w-full text-left bg-white rounded-2xl border-2 px-3 py-2.5 ${n.read ? "border-[#FDECEF]" : "border-sorted-peach"}`}>
                    <p className="text-sm font-bold text-sorted-ink">{n.title}</p>
                    <p className="text-xs text-sorted-slate">{n.body}</p>
                  </button>
                ))}
              </div>
            )}

            {!recap ? (
              <div className="mt-6 text-center">
                <p className="text-sm text-sorted-slate">No recap yet this week — Millie writes one every Sunday arvo. Want a sneak peek now?</p>
                <button onClick={make} disabled={loading} data-testid="recap-generate" className="mt-4 bg-sorted-rose text-white font-bold px-5 py-3 rounded-full flex items-center gap-2 mx-auto">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2.5} />} Write my recap
                </button>
              </div>
            ) : (
              <div className="mt-5" data-testid="recap-body">
                <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-5 shadow-soft">
                  <p className="text-4xl">{recap.emoji}</p>
                  <h2 className="font-heading text-xl font-semibold text-sorted-ink mt-2" data-testid="recap-headline">{recap.headline}</h2>
                  <p className="text-sm text-sorted-slate mt-2 leading-relaxed">{recap.note}</p>
                </div>
                {noticed.length > 0 && (
                  <div className="mt-4 bg-[#DEE7F5] rounded-3xl border-2 border-white/60 p-4" data-testid="recap-noticed">
                    <p className="font-heading font-semibold text-sorted-ink text-sm flex items-center gap-1.5"><Eye size={15} strokeWidth={2.5} className="text-blue-500" /> Millie noticed this week</p>
                    <p className="text-[11px] text-sorted-slate font-semibold mt-0.5">Keep the ones that are true and I'll plan around them. Forget the rest.</p>
                    <div className="mt-3 space-y-2">
                      {noticed.map((m) => (
                        <div key={m.id} className="bg-white/90 rounded-2xl px-3 py-2 flex items-center gap-2" data-testid={`noticed-${m.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-sorted-ink leading-tight">{m.member ? <b>{m.member}: </b> : null}{m.text}</p>
                            <p className="text-[11px] text-sorted-slate font-semibold capitalize">{m.type.replace("_", " ")}{m.day ? ` · ${m.day}s` : ""}{m.times_seen > 1 ? ` · seen ${m.times_seen}×` : ""}</p>
                          </div>
                          <button onClick={() => keep(m)} data-testid={`noticed-keep-${m.id}`} aria-label="Keep" className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center"><Check size={14} strokeWidth={3} /></button>
                          <button onClick={() => forget(m)} data-testid={`noticed-forget-${m.id}`} aria-label="Forget" className="w-8 h-8 rounded-full bg-white border-2 border-[#FDECEF] text-sorted-slate flex items-center justify-center"><Trash2 size={13} strokeWidth={2.5} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {recap.children?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {recap.children.map((c) => (
                      <div key={c.name} className="bg-white rounded-2xl border-2 border-[#FDECEF] p-3 flex items-center gap-3" data-testid={`recap-child-${c.name}`}>
                        <span className="w-9 h-9 rounded-full bg-sorted-sage flex items-center justify-center text-sorted-ink"><Star size={16} strokeWidth={2.5} /></span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sorted-ink text-sm">{c.name} · {c.liked + c.tried} little wins</p>
                          <p className="text-xs text-sorted-slate truncate">{c.foods.join(", ") || "Nothing new yet — that's okay"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {recap.next_week?.length > 0 && (
                  <div className="mt-4">
                    <p className="font-heading font-semibold text-sorted-ink text-sm mb-2">Next week's dinners</p>
                    <div className="flex flex-wrap gap-1.5">
                      {recap.next_week.map((d) => <span key={d.day} className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-[11px] font-bold px-2.5 py-1 rounded-full">{d.day.slice(0, 3)} · {d.title}</span>)}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button onClick={() => { onClose(); navigate("/wins"); }} data-testid="recap-to-wins" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full text-sm">Little Wins</button>
                  <button onClick={() => { onClose(); navigate("/meals"); }} data-testid="recap-to-plan" className="bg-sorted-rose text-white font-bold py-3 rounded-full text-sm">See the week</button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
